import type { JSX } from 'react';

import { Box, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { PlaceType } from '@/api/types/Place';

// Type chip for the Adresář (prototype `.chip.load` / `.chip.unload` /
// `.chip.both`). Semantics fixed by `zadani.md §2`:
//   Nakládka = primary (green — the CHR-04 route-stop „load" colour),
//   Vykládka = secondary (earthy brown — route-stop „unload"),
//   Obojí    = neutral `text.secondary` grey.
// User request 2026-09-17 („změň barvy labelů vykládky nakládky na sytější — ať
// to jde líp vidět"): all three are SOLID fills with the light text on top,
// replacing the soft tinted backgrounds of the prototype. Contrast on the
// theme palette stays AA: green 5.2:1, brown 4.7:1, grey 4.9:1 against
// `#FFFFFF`. This is the one place the three pairings live — the table, the
// cards, the autocomplete options and the page legend all render this chip.
// A dedicated component rather than a `BrandChip` variant because the brown
// pairing exists nowhere else in the app and would widen a shared API for one
// caller.

const CHIP_FONT_SIZE = 11.5;

export interface PlaceTypeChipProps {
  type: PlaceType;
  'data-testid'?: string;
}

export const PlaceTypeChip = ({
  type,
  'data-testid': dataTestId,
}: PlaceTypeChipProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('places');

  const palette =
    type === 'LOADING'
      ? {
          bg: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        }
      : type === 'UNLOADING'
        ? {
            bg: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
          }
        : {
            bg: theme.palette.text.secondary,
            color: theme.palette.background.paper,
          };

  return (
    <Box
      component="span"
      data-testid={dataTestId}
      data-type={type}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.25,
        py: 0.375,
        borderRadius: 999,
        fontSize: CHIP_FONT_SIZE,
        fontWeight: 600,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        bgcolor: palette.bg,
        color: palette.color,
      }}
    >
      {t(`type.${type}`)}
    </Box>
  );
};
